<template>
  <v-container fluid class="atch-settings-page">
    <AError :api="apiUpdateAttachment" />

    <!-- Alert errors -->
    <AAlertBanner :messages="alertError" title="Warning" icon="mdi-alert" :icon-size="22" />

    <!-- Header -->
    <div class="atch-header">
      <div class="atch-header__top">

        <div v-if="!loadingDialog" class="atch-header__info">
          <h1 class="atch-header__title">{{ model.name.plural }}</h1>
        </div>

        <div class="atch-header__actions">
          <span v-if="isHrdcSuperAdminRoles">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="hero-action-btn hero-action-btn--blue"
                  @click="toggleUpdateAtchDialog(true)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <!-- Heroicon: arrow-up-tray -->
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="hero-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                  </svg>
                </button>
              </template>
              <span>Update {{ model.name.plural }}</span>
            </v-tooltip>
          </span>
        </div>
      </div>
    </div>

    <!-- File Size Alert -->
    <UploadAttachmentSettingFileSizeAlert></UploadAttachmentSettingFileSizeAlert>

    <!-- Attachment Cards -->
    <div class="tier-grid">
      <!-- Guideline Card -->
      <div class="tier-card">
        <div class="tier-card__header">
          <div class="tier-card__left">
            <span class="tier-card__price">Guideline</span>
          </div>
        </div>
        <div class="tier-card__body">
          <div
            class="tier-file"
            @drop.prevent="addDropFile($event, 'guideline', true)"
            @dragover.prevent
          >
            <div class="tier-file__icon tier-file__icon--pdf">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="tier-file__svg">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </div>
            <div class="tier-file__info">
              <span class="tier-file__label">PDF Document</span>
              <span class="tier-file__name">{{ formData.guideline ? formData.guideline.name : 'No file selected' }}</span>
            </div>
            <div class="tier-file__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="tier-action-btn tier-action-btn--upload" for="guideline-upload" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="tier-action-icon">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
                    </svg>
                    <input
                      type="file"
                      id="guideline-upload"
                      class="tier-file__hidden-input"
                      accept=".pdf"
                      @change="formData.guideline = $event.target.files[0]"
                    />
                  </label>
                </template>
                <span>Upload File</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <button class="tier-action-btn tier-action-btn--preview" @click="previewAttachment('guideline')" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="tier-action-icon">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  </button>
                </template>
                <span>Preview File</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.guideline" class="tier-file__error">{{ errorField.guideline }}</div>
        </div>
      </div>

      <!-- Agenda Card -->
      <div class="tier-card">
        <div class="tier-card__header">
          <div class="tier-card__left">
            <span class="tier-card__price">Course Content (Agenda)</span>
          </div>
        </div>
        <div class="tier-card__body">
          <div
            class="tier-file"
            @drop.prevent="addDropFile($event, 'agenda', true)"
            @dragover.prevent
          >
            <div class="tier-file__icon tier-file__icon--pdf">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="tier-file__svg">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </div>
            <div class="tier-file__info">
              <span class="tier-file__label">PDF Document</span>
              <span class="tier-file__name">{{ formData.agenda ? formData.agenda.name : 'No file selected' }}</span>
            </div>
            <div class="tier-file__actions">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <label class="tier-action-btn tier-action-btn--upload" for="agenda-upload" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="tier-action-icon">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
                    </svg>
                    <input
                      type="file"
                      id="agenda-upload"
                      class="tier-file__hidden-input"
                      accept=".pdf"
                      @change="formData.agenda = $event.target.files[0]"
                    />
                  </label>
                </template>
                <span>Upload File</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <button class="tier-action-btn tier-action-btn--preview" @click="previewAttachment('agenda')" v-on="on">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="tier-action-icon">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  </button>
                </template>
                <span>Preview File</span>
              </v-tooltip>
            </div>
          </div>
          <div v-if="errorField.agenda" class="tier-file__error">{{ errorField.agenda }}</div>
        </div>
      </div>
    </div>

    <TheModalConfirmation
      ref="updateAtchConfirmPopup"
      @agree="agreeCallbacUpdateAtch()"
      @disagree="disagreeCallbacUpdateAtch()"
    ></TheModalConfirmation>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";
import UploadAttachmentSettingFileSizeAlert from "@/components/views/UploadAttachmentSettingFileSizeAlert.vue";

export default {
  components: {
    TheModalConfirmation,
    UploadAttachmentSettingFileSizeAlert,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  data: () => ({
    apiUpdateAttachment: new Api(),
    apiGetAttachmentBlob: new Api(),
    model: new Model(),
    service: new Service(),
    alertError: [],
    errorLogDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    formData: {
      guideline: null,
      agenda: null,
    },
    errorField: {
      guideline: null,
      agenda: null,
    },
    staticAttachments: [],
    loadingDialog: false,
  }),
  async created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    this.serviceDataAssignation(this.service.key);

    const apiDetails = {
      apiGetAttachmentBlob: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-attachment-blob`,
      },
      apiUpdateAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/attachment/update-attachment`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].query !== undefined) {
        this[api].setQuery(apiDetails[api].query);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    // Assign fake pdf data.
    this.formData = {
      guideline: { name: `${this.guidelineName}.pdf` },
      agenda: { name: `${this.agendaAtchName}.pdf` },
    };

    this.rolesAssignation(this.auth.roles);
  },
  methods: {
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    redirectBack() {
      let route = this.model.parent_route;
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
      });
    },
    addDropFile(e, key, strictPdf = true) {
      let file = e.dataTransfer.files[0];

      if (strictPdf) {
        if (!file.type.match("pdf.*")) {
          this.errorField[key] = "Please select pdf file format";
          return;
        }
      }

      this.formData[key] = file;
    },
    previewAttachment(fileType) {
      // Check is empty file.
      if (!this.formData[fileType]) {
        this.$store.dispatch("showMessage", {
          message: "No Preview Available",
          messageType: "info",
          timeout: 2000,
        });
        return false;
      }

      // If user has selected a new file, preview the local file directly.
      if (this.formData[fileType] instanceof File) {
        this.previewBlobAttachment(fileType);
        return;
      }

      this.showLoadingDialog();

      const fileUrl = `assets/storage/${this.formData[fileType].name}`;

      this.$axios
        .get(this.apiGetAttachmentBlob.url, {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            filePath: fileUrl,
          },
        })
        .then((response) => {
          const blobUrl = URL.createObjectURL(
            new Blob([response.data], { type: "application/pdf" })
          );

          window.open(blobUrl, "_blank");
        })
        .catch(() => {
          this.previewBlobAttachment(fileType);
        })
        .finally(() => {
          this.hideLoadingDialog();
        });
    },
    async previewBlobAttachment(fileType) {
      this.showLoadingDialog();

      let fileUrl = this.formData[fileType];

      if (!fileUrl) {
        this.$store.dispatch("showMessage", {
          message: "No Preview Available",
          messageType: "info",
        });
        this.hideLoadingDialog();
        return;
      }

      let url = null;
      let newWindow = null;
      switch (fileType) {
        default:
          url = URL.createObjectURL(fileUrl);
          newWindow = window.open(url, "_blank");
          break;
      }

      if (newWindow) {
        newWindow.onload = function () {
          // Revoke the object URL when the window is closed
          newWindow.addEventListener("beforeunload", function () {
            URL.revokeObjectURL(url);
          });
        };
      }

      this.hideLoadingDialog();
    },
    getOriFileName(file) {
      let fileName = null;

      switch (file) {
        case "guideline":
          fileName = `${this.guidelineName}.pdf`;
          break;

        case "agenda":
          fileName = `${this.agendaAtchName}.pdf`;
          break;
      }

      return fileName;
    },
    toggleUpdateAtchDialog(val) {
      if (val) {
        this.$refs.updateAtchConfirmPopup.open({
          title: "Update Attachment",
          message: "Are you confirm to update attachment?",
          options: {
            btnDisagree: "Cancel",
            btnAgree: "Update",
          },
          agree: () => {
            this.agreeCallbacUpdateAtch();
          },
          disagree: () => {
            this.disagreeCallbacUpdateAtch();
          },
        });
      }
    },
    agreeCallbacUpdateAtch() {
      this.showLoadingDialog();
      this.clearErrorMsg();

      // Check is file fields empty.
      let isUploadContainsEmpty = false;
      Object.keys(this.formData).forEach((field) => {
        if (!this.formData[field]) {
          this.errorField[field] = `${this.capitalizeFirstLetter(
            field
          )} is required`;
          isUploadContainsEmpty = true;
        }
      });

      if (isUploadContainsEmpty) {
        this.hideLoadingDialog();
        return false;
      }

      const fd = new FormData();

      // Only append File instance.
      for (const atch in this.formData) {
        if (this.formData[atch] instanceof File) {
          fd.append(
            `${atch}_filepath`,
            `assets/storage/${this.getOriFileName(atch)}`
          );
          fd.append(atch, this.formData[atch]);
        }
      }

      const fdLength = [...fd.entries()].length;
      if (fdLength == 0) {
        this.$store.dispatch("showMessage", {
          message: "No changes apply!",
          messageType: "info",
          timeout: 2000,
        });

        this.hideLoadingDialog();
        return;
      }

      // Only append static datas after checking.
      fd.append("staticFiles", this.staticEventDocs);

      this.apiUpdateAttachment.setParams(fd);
      this.apiUpdateAttachment.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;

          let messageType = "success";
          if (!status) {
            messageType = "errror";
          }

          this.$store.dispatch("showMessage", {
            message: data,
            messageType,
            timeout: 2000,
          });
        } catch (err) {
          console.log(err);
        } finally {
          this.hideLoadingDialog();
        }
      });
      this.apiUpdateAttachment.fetch();
    },
    disagreeCallbacUpdateAtch() {
      //
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .atch-settings-page {
  background-color: var(--app-page);
}

.dark-mode .atch-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--blue {
  background: var(--app-accent-soft);
}

.dark-mode .hero-action-btn--blue:hover {
  background: var(--app-accent-soft);
}

.dark-mode .tier-card {
  background: var(--app-card);
}

.dark-mode .tier-card__header {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .tier-card__price {
  color: var(--app-text);
}

.dark-mode .tier-file {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .tier-file:hover {
  border-color: var(--app-border);
}

.dark-mode .tier-file__icon--pdf {
  background: var(--app-danger-bg);
}

.dark-mode .tier-file__label {
  color: var(--app-text-muted);
}

.dark-mode .tier-file__name {
  color: var(--app-text);
}

.dark-mode .tier-action-btn--upload {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .tier-action-btn--upload:hover {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .tier-action-btn--preview {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .tier-action-btn--preview:hover {
  background: var(--app-elevated);
  color: var(--app-text);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.atch-settings-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  zoom: 1.1;
}

/* ============================================
   HEADER
   ============================================ */
.atch-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.atch-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.atch-header__info {
  flex: 1;
  min-width: 0;
}

.atch-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.atch-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 16px;
}

/* ============================================
   HERO ACTION BUTTONS
   ============================================ */
.hero-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.hero-action-btn:hover {
  background: #e2e8f0;
  color: #334155;
  transform: translateY(-1px);
}

.hero-icon {
  width: 18px;
  height: 18px;
}

.hero-action-btn--blue {
  background: #eff6ff;
  color: #3b82f6;
}

.hero-action-btn--blue:hover {
  background: #dbeafe;
  color: #2563eb;
}

/* ============================================
   ATTACHMENT CARDS
   ============================================ */
.tier-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
}

.tier-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.tier-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* Tier header */
.tier-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #f1f5f9;
}

.tier-card__left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.tier-card__price {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.5px;
  font-family: 'Lato', sans-serif;
}

/* Tier body */
.tier-card__body {
  padding: 20px;
}

/* File row */
.tier-file {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  transition: border-color 0.15s;
}

.tier-file:hover {
  border-color: #e2e8f0;
}

.tier-file__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tier-file__icon--pdf {
  background: #fef2f2;
  color: #ef4444;
}

.tier-file__svg {
  width: 20px;
  height: 20px;
}

.tier-file__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tier-file__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.tier-file__name {
  font-size: 0.82rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tier-file__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.tier-file__hidden-input {
  display: none;
}

.tier-action-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.tier-action-btn--upload {
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
}

.tier-action-btn--upload:hover {
  background: #e2e8f0;
  color: #334155;
}

.tier-action-btn--preview {
  background: #f1f5f9;
  color: #64748b;
}

.tier-action-btn--preview:hover {
  background: #e2e8f0;
  color: #334155;
}

.tier-action-icon {
  width: 18px;
  height: 18px;
}

.tier-file__error {
  margin-top: 6px;
  padding-left: 54px;
  font-size: 0.78rem;
  color: #ef4444;
  font-family: 'Lato', sans-serif;
}

/* ============================================
   FADE TRANSITION
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   MOBILE RESPONSIVE
   ============================================ */
@media (max-width: 599px) {
  .atch-settings-page {
    padding: 12px 8px !important;
    zoom: 1;
    overflow-x: hidden;
  }

  .atch-header {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .atch-header__top {
    flex-wrap: wrap;
    padding: 16px 16px 12px;
  }

  .back-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 12px;
  }

  .atch-header__title {
    font-size: 1.05rem;
    white-space: normal;
  }

  .atch-header__actions {
    width: 100%;
    margin-left: 0;
    margin-top: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
  }

  .hero-action-btn {
    width: 36px;
    height: 36px;
  }

  .tier-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .tier-card {
    border-radius: 14px;
  }

  .tier-card__header {
    padding: 14px 16px;
  }

  .tier-card__body {
    padding: 14px;
  }

  .tier-file {
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 12px;
  }

  .tier-file__info {
    flex: 1 1 calc(100% - 54px);
  }

  .tier-file__actions {
    width: 100%;
    justify-content: flex-end;
  }
}

/* Tablet breakpoint */
@media (min-width: 600px) and (max-width: 959px) {
  .atch-settings-page {
    zoom: 1;
  }

  .atch-header__top {
    padding: 20px 20px 14px;
  }

  .atch-header__title {
    font-size: 1.15rem;
  }

  .atch-header__actions {
    flex-wrap: wrap;
    gap: 4px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .atch-settings-page {
  background-color: var(--app-page);
}

.dark-mode .atch-header {
  background: var(--app-page);
}

.dark-mode .atch-header__title {
  color: var(--app-text);
}

.dark-mode .back-btn {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .back-btn:hover {
  background: var(--app-card);
  border-color: var(--app-border-strong);
}

.dark-mode .tier-card {
  background: var(--app-page);
  box-shadow: 0 1px 4px var(--app-shadow-soft);
}

.dark-mode .tier-card__header {
  border-bottom-color: var(--app-border);
}

.dark-mode .tier-card__price {
  color: var(--app-text);
}

.dark-mode .tier-card__body {
  background: var(--app-page);
}

.dark-mode .tier-file {
  background: var(--app-card);
  border-color: var(--app-border);
}

.dark-mode .tier-file__icon--pdf {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
}

.dark-mode .tier-file__label {
  color: var(--app-text-faint);
}

.dark-mode .tier-file__name {
  color: var(--app-neutral-fg);
}

.dark-mode .tier-action-btn--upload {
  background: var(--app-card);
  color: var(--app-text-muted);
}

.dark-mode .tier-action-btn--upload:hover {
  background: var(--app-elevated);
  color: var(--app-neutral-fg);
}

.dark-mode .tier-action-btn--preview {
  background: var(--app-card);
  color: var(--app-text-muted);
}

.dark-mode .tier-action-btn--preview:hover {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
}

.dark-mode .hero-action-btn {
  background: var(--app-card);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-elevated);
  color: var(--app-neutral-fg);
}

.dark-mode .hero-action-btn--blue {
  background: var(--app-accent-soft);
  color: var(--app-accent);
}

.dark-mode .hero-action-btn--blue:hover {
  background: var(--app-accent-soft);
  color: var(--app-accent-strong);
}
</style>
